import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { Printer, Camera } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';

const esc = (s = '') => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const getLabel = (obj, champs = ['intitule', 'nom', 'name', 'code', 'code_filiere']) => {
  if (!obj) return '-';
  for (const c of champs) {
    if (obj[c]) return obj[c];
  }
  return '-';
};

const photoCandidates = (e) => {
  const list = [];
  const dl = e.documents?.photo?.download_url;
  if (dl) list.push(dl);
  const id = e._id || e.etudiant_id;
  const cne = e.CNE;
  ['jpeg', 'jpg', 'png', 'JPEG', 'JPG', 'PNG'].forEach(x => {
    if (id) list.push(`/uploads/etudiants/${id}_photo.${x}`);
    if (cne) list.push(`/uploads/etudiants/${cne}.${x}`);
  });
  return list;
};

const PhotoImg = ({ e }) => {
  const cands = useMemo(() => photoCandidates(e), [e]);
  const [i, setI] = useState(0);
  const src = cands[i];
  if (!src) return <span style={{ fontSize: '1.4rem', fontWeight: 800, color: '#4f46e5' }}>{initials(e)}</span>;
  return <img src={src} alt="photo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setI(x => x + 1)} />;
};

const initials = (e) => {
  const a = (e.Prénom || '')[0] || '';
  const b = (e.Nom || '')[0] || '';
  return (a + b).toUpperCase() || '?';
};

const getKey = (obj) => String(obj?._id || obj?.filiere_id || obj?.niveau_id || obj?.annee_id || obj?.id || obj?.code || '');

const anneeC = (a) => [a?._id, a?.annee_id, a?.id, a?.intitule, a?.nom, a?.name].map(v => v ? String(v) : null).filter(Boolean);
const filiereC = (f) => [f?._id, f?.filiere_id, f?.id, f?.code_filiere, f?.intitule, f?.nom, f?.name].map(v => v ? String(v) : null).filter(Boolean);
const niveauC = (n) => [n?._id, n?.niveau_id, n?.id, n?.code, n?.intitule, n?.nom, n?.name].map(v => v ? String(v) : null).filter(Boolean);

const flagVals = (v) => v ? (Array.isArray(v) ? v : [v]) : [];

const anneeOf = (i) => {
  const out = [];
  for (const v of flagVals(i?.annee_universitaire_id)) out.push(typeof v === 'object' ? [v._id, v.annee_id, v.id, v.intitule, v.nom, v.name] : v);
  for (const v of flagVals(i?.anneeUniversitaire)) out.push(typeof v === 'object' ? [v._id, v.annee_id, v.id, v.intitule, v.nom, v.name] : v);
  return out.flat().filter(Boolean).map(String);
};

const filiereOf = (i) => {
  const out = [];
  for (const v of flagVals(i?.filiere_id)) out.push(typeof v === 'object' ? [v._id, v.filiere_id, v.id, v.code_filiere, v.intitule] : v);
  for (const v of flagVals(i?.filiere)) out.push(typeof v === 'object' ? [v._id, v.filiere_id, v.id, v.code_filiere, v.intitule] : v);
  return out.flat().filter(Boolean).map(String);
};

const niveauOf = (i) => {
  const out = [];
  for (const v of flagVals(i?.niveau_id)) out.push(typeof v === 'object' ? [v._id, v.niveau_id, v.id, v.code, v.intitule] : v);
  for (const v of flagVals(i?.niveau)) out.push(typeof v === 'object' ? [v._id, v.niveau_id, v.id, v.code, v.intitule] : v);
  return out.flat().filter(Boolean).map(String);
};

const etuOf = (i) => {
  const out = [];
  for (const v of flagVals(i?.etudiant_id)) out.push(typeof v === 'object' ? [v._id, v.etudiant_id, v.id] : v);
  for (const v of flagVals(i?.etudiant)) out.push(typeof v === 'object' ? [v._id, v.etudiant_id, v.id] : v);
  return out.flat().filter(Boolean).map(String);
};

const intersect = (as, bs) => (as || []).some(a => (bs || []).indexOf(a) !== -1);

const normLbl = (s) => String(s || '').toLowerCase().trim().replace(/\s+/g, ' ');

const uniqByLabel = (arr) => {
  const m = new Map();
  (arr || []).forEach(x => {
    const key = `label:${normLbl(getLabel(x))}`;
    if (!m.has(key)) m.set(key, x);
  });
  return Array.from(m.values());
};

const uniqById = (arr, champs) => {
  const m = new Map();
  (arr || []).forEach(x => {
    let key = '';
    for (const c of champs) {
      if (x[c]) { key = `id:${String(x[c])}`; break; }
    }
    if (!key) key = `label:${getLabel(x).toLowerCase()}`;
    if (!m.has(key)) m.set(key, x);
  });
  return Array.from(m.values());
};

const CarteVisuelle = ({ e, selected, onToggle, onUpload }) => {
  const fileRef = useRef(null);
  const nomComplet = `${e.Nom || ''} ${e.Prénom || ''}`.trim() || e.CNE || 'Étudiant';
  const filiere = getLabel(e.filiere);
  const niveau = getLabel(e.niveau).replace('L1', '1ère année').replace('L2', '2ème année').replace('L3', '3ème année');
  const annee = getLabel(e.annee);
  const numero = e.inscription?.numeroInscription || '';
  const cne = e.CNE || '-';
  const sexe = e.sexe || '';
  const naissance = e.date_naissance || '';

  return (
    <div
      onClick={() => onToggle && onToggle(e)}
      style={{
        width: '320px',
        minHeight: '188px',
        background: '#fff',
        borderRadius: '16px',
        boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
        cursor: onToggle ? 'pointer' : 'default',
        position: 'relative',
        overflow: 'hidden',
        border: selected ? '3px solid #3b82f6' : '2px solid #e5e7eb'
      }}
    >
      {onToggle && (
        <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 5, display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.92)', borderRadius: '8px', padding: '2px 6px', boxShadow: '0 1px 4px rgba(0,0,0,0.15)' }}>
          <input type="checkbox" checked={!!selected} readOnly />
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'linear-gradient(135deg, #6d28d9, #2563eb)', color: '#fff', padding: '12px 14px' }}>
        <img src="/images/logo_sup2i.png" alt="SUP2I" style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#fff', objectFit: 'contain' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: '0.95rem', letterSpacing: '0.02em' }}>SUP2I</div>
          <div style={{ fontSize: '0.62rem', opacity: 0.9 }}>École Supérieure d'Informatique</div>
        </div>
        <div style={{ fontSize: '0.62rem', textAlign: 'right', lineHeight: '1.3' }}>
          CARTE<br />ÉTUDIANT
          <div style={{ fontSize: '0.6rem', opacity: 0.85 }}>{annee}</div>
        </div>
      </div>
      <div style={{ display: 'flex', padding: '12px 14px', gap: '12px' }}>
        <div
          onClick={(ev) => { if (onUpload) { ev.stopPropagation(); fileRef.current && fileRef.current.click(); } }}
          title={onUpload ? 'Cliquer pour ajouter / modifier la photo' : ''}
          style={{ position: 'relative', width: '64px', height: '64px', borderRadius: '10px', background: '#eef2ff', border: '1px solid #dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0, cursor: onUpload ? 'pointer' : 'default' }}
        >
          <PhotoImg e={e} />
          {onUpload && (
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(17,24,39,0.72)', color: '#fff', fontSize: '0.5rem', textAlign: 'center', padding: '1px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
              <Camera size={9} /> Photo
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onClick={(ev) => ev.stopPropagation()}
            onChange={(ev) => { const f = ev.target.files && ev.target.files[0]; if (f && onUpload) onUpload(e, f); ev.target.value = ''; }}
          />
        </div>
        <div style={{ flex: 1, fontSize: '0.74rem', color: '#374151', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#111827' }}>{nomComplet}</div>
          <div style={{ color: '#6b7280', fontSize: '0.68rem' }}>{filiere} · {niveau}</div>
          <div style={{ marginTop: '2px' }}><strong>CNE :</strong> {cne}</div>
          <div><strong>N° Inscription :</strong> {numero || '-'}</div>
          {naissance && <div><strong>Né(e) le :</strong> {naissance}</div>}
          {sexe && <div><strong>Sexe :</strong> {sexe === 'M' ? 'Masculin' : sexe === 'F' ? 'Féminin' : sexe}</div>}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', borderTop: '1px dashed #e5e7eb', color: '#6b7280', fontSize: '0.62rem' }}>
        <span>Année universitaire {annee}</span>
        <span style={{ letterSpacing: '0.12em', fontWeight: 600 }}>{cne}</span>
      </div>
    </div>
  );
};

const CartesEtudiants = () => {
  const { user } = usePermissions();
  // Ajout / modification d'une photo : réservé à ceux qui gèrent les étudiants.
  const peutGererEtudiants = ['ADMIN', 'DIRECTEUR', 'SCOLARITE'].includes(user?.role);

  const [etudiants, setEtudiants] = useState([]);
  const [inscriptions, setInscriptions] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [filtreFiliere, setFiltreFiliere] = useState('all');
  const [filtreNiveau, setFiltreNiveau] = useState('all');
  const [filtreAnnee, setFiltreAnnee] = useState('all');

  const [selectedIds, setSelectedIds] = useState({});

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [etudiantsRes, inscriptionsRes, filieresRes, niveauxRes, anneesRes] = await Promise.all([
        axios.get('/api/etudiants?limit=100000', { headers, timeout: 20000 }).catch(() => ({ data: { data: [] } })),
        axios.get('/api/inscriptions', { headers, timeout: 15000 }).catch(() => ({ data: [] })),
        axios.get('/api/filieres', { headers, timeout: 15000 }).catch(() => ({ data: [] })),
        axios.get('/api/niveaux', { headers, timeout: 15000 }).catch(() => ({ data: [] })),
        axios.get('/api/annees', { headers, timeout: 15000 }).catch(() => ({ data: [] }))
      ]);

      const etud = Array.isArray(etudiantsRes.data) ? etudiantsRes.data : (etudiantsRes.data?.data || []);
      const insc = Array.isArray(inscriptionsRes.data) ? inscriptionsRes.data : (inscriptionsRes.data?.data || []);
      const fil = Array.isArray(filieresRes.data) ? filieresRes.data : (filieresRes.data?.data || []);
      const niv = Array.isArray(niveauxRes.data) ? niveauxRes.data : (niveauxRes.data?.data || []);
      const ann = Array.isArray(anneesRes.data) ? anneesRes.data : (anneesRes.data?.data || []);

      setEtudiants(etud);
      setInscriptions(insc);
      setFilieres(fil);
      setNiveaux(niv);
      setAnnees(ann);
      setError('');
    } catch (err) {
      console.error('Erreur chargement cartes:', err);
      setError('Impossible de charger les données des étudiants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!info) return;
    const t = setTimeout(() => setInfo(''), 4000);
    return () => clearTimeout(t);
  }, [info]);

  const uploadPhoto = async (etudiant, file) => {
    const id = etudiant._id || etudiant.id || etudiant.etudiant_id;
    if (!id) return;
    if (!/^image\//.test(file.type)) {
      setInfo('');
      setError('Veuillez choisir un fichier image (jpg, png…)');
      return;
    }
    try {
      setError('');
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`/api/etudiants/upload/photo/${id}`, form, { headers, timeout: 30000 });
      const doc = (res.data && res.data.document) || {};
      const url = doc.download_url ? `${doc.download_url}?v=${Date.now()}` : null;
      setEtudiants(prev => prev.map(x => {
        const xid = x._id || x.etudiant_id;
        if (String(xid) !== String(id)) return x;
        return { ...x, documents: { ...(x.documents || {}), photo: { ...doc, download_url: url } } };
      }));
      setInfo(`Photo mise à jour : ${`${etudiant.Nom || ''} ${etudiant.Prénom || ''}`.trim() || etudiant.CNE || ''}`);
    } catch (err) {
      console.error('Erreur upload photo:', err);
      setInfo('');
      setError((err.response && err.response.data && err.response.data.error) || 'Impossible de mettre à jour la photo');
    }
  };

  const filieresUniq = useMemo(() => uniqById(filieres, ['_id', 'filiere_id']), [filieres]);
  const niveauxUniq = useMemo(() => uniqById(niveaux, ['_id', 'niveau_id']), [niveaux]);
  const anneesUniq = useMemo(() => uniqById(annees, ['_id', 'annee_id']), [annees]);

  const inscUniq = useMemo(() => {
    const m = new Map();
    (inscriptions || []).forEach(i => {
      const key = `${etuOf(i).join('|')}::${anneeOf(i).join('|')}::${filiereOf(i).join('|')}::${niveauOf(i).join('|')}`;
      if (!m.has(key)) m.set(key, i);
    });
    return Array.from(m.values());
  }, [inscriptions]);

  const selAnneeDoc = useMemo(() => filtreAnnee === 'all' ? null : anneesUniq.find(a => anneeC(a).includes(filtreAnnee) || getLabel(a) === filtreAnnee) || null, [anneesUniq, filtreAnnee]);
  const selFiliereDoc = useMemo(() => filtreFiliere === 'all' ? null : filieresUniq.find(f => filiereC(f).includes(filtreFiliere) || getLabel(f) === filtreFiliere) || null, [filieresUniq, filtreFiliere]);
  const niveauGroups = useMemo(() => {
    const pool = [...niveauxUniq];
    (filieres || []).forEach(f => (f.niveaux || []).forEach(sub => {
      const subId = String(sub.niveau_id || sub._id || '');
      const has = pool.some(n => (niveauC(n).indexOf(subId) !== -1 && subId) || normLbl(getLabel(n)) === normLbl(getLabel(sub)));
      if (!has) {
        pool.push({ niveau_id: sub.niveau_id, code: sub.code, intitule: sub.intitule || sub.nom || sub.name || sub.code || sub.niveau_id, ordre: sub.ordre, _embedded: true });
      }
    }));
    const groups = [];
    const byId = new Map();
    const byLbl = new Map();
    for (const it of pool) {
      const ids = niveauC(it);
      const lbl = normLbl(getLabel(it));
      let g = null;
      for (const id of ids) { if (byId.has(id)) { g = byId.get(id); break; } }
      if (!g && lbl) g = byLbl.get(lbl) || null;
      const fresh = g || (() => { const x = { items: [], ids: new Set(), labels: new Set() }; groups.push(x); return x; })();
      ids.forEach(id => { if (id) { fresh.ids.add(id); byId.set(id, fresh); } });
      if (lbl) { fresh.labels.add(lbl); byLbl.set(lbl, fresh); }
      fresh.items.push(it);
    }
    return groups;
  }, [niveauxUniq, filieres]);

  const bestNiveau = (g) => g.items.find(x => x._id) || g.items[0];

  const selNiveauGroup = useMemo(() => filtreNiveau === 'all' ? null : niveauGroups.find(g => g.items.some(n => niveauC(n).indexOf(filtreNiveau) !== -1 || getKey(n) === filtreNiveau)) || null, [niveauGroups, filtreNiveau]);

  const insAnneeOK = (i) => filtreAnnee === 'all' || (selAnneeDoc ? intersect(anneeC(selAnneeDoc), anneeOf(i)) : false);
  const insFiliereOK = (i) => filtreFiliere === 'all' || (selFiliereDoc ? intersect(filiereC(selFiliereDoc), filiereOf(i)) : false);
  const insNiveauOK = (i) => filtreNiveau === 'all' || (selNiveauGroup ? selNiveauGroup.items.some(n => intersect(niveauC(n), niveauOf(i))) : false);

  const filieresDispo = useMemo(() => uniqByLabel(filieresUniq.filter(f =>
    inscUniq.some(i => insAnneeOK(i) && intersect(filiereC(f), filiereOf(i)))
  )), [filieresUniq, inscUniq, selAnneeDoc, filtreAnnee]);

  const niveauxDispo = useMemo(() => {
    if (filtreFiliere === 'all') return niveauGroups.map(bestNiveau);
    const qualified = new Set();
    niveauxUniq.forEach(n => {
      if (inscUniq.some(i => insAnneeOK(i) && insFiliereOK(i) && intersect(niveauC(n), niveauOf(i)))) qualified.add(String(n._id || n.niveau_id || ''));
    });
    const embIds = new Set(((selFiliereDoc && selFiliereDoc.niveaux) || []).map(s => String(s.niveau_id || s._id || '')));
    return niveauGroups.filter(g =>
      g.items.some(n => embIds.has(String(n.niveau_id || n._id || '')) || qualified.has(String(n._id || n.niveau_id || '')))
    ).map(bestNiveau);
  }, [niveauGroups, niveauxUniq, inscUniq, selAnneeDoc, selFiliereDoc, filtreAnnee, filtreFiliere]);

  const anneesDispo = useMemo(() => uniqByLabel(anneesUniq), [anneesUniq]);

  const etudiantsUniq = useMemo(() => {
    const m = new Map();
    (etudiants || []).forEach(e => {
      const k = e.CNE ? `cne:${String(e.CNE).trim().toLowerCase()}` : `id:${String(e._id || e.etudiant_id || '')}`;
      if (!m.has(k)) m.set(k, e);
    });
    return Array.from(m.values());
  }, [etudiants]);

  const enriched = useMemo(() => {
    return etudiantsUniq.map(e => {
      const id = e._id || e.etudiant_id;
      const insList = inscUniq.filter(i => etuOf(i).indexOf(String(id)) !== -1);
      const matched = insList.some(i => insAnneeOK(i) && insFiliereOK(i) && insNiveauOK(i));
      let pool = insList.filter(i => insAnneeOK(i) && insFiliereOK(i) && insNiveauOK(i));
      if (!pool.length) pool = insList;
      const ins = [...pool].sort((a, b) => {
        const pa = String(a.statut || '').toUpperCase() === 'CONFIRMEE' ? 0 : 1;
        const pb = String(b.statut || '').toUpperCase() === 'CONFIRMEE' ? 0 : 1;
        if (pa !== pb) return pa - pb;
        return new Date(b.date_inscription || b.createdAt || 0) - new Date(a.date_inscription || a.createdAt || 0);
      })[0] || null;
      const filiere = ins ? filieresUniq.find(f => intersect(filiereC(f), filiereOf(ins))) || null : null;
      const niveau = ins ? niveauxUniq.find(n => intersect(niveauC(n), niveauOf(ins))) || null : null;
      const annee = ins ? anneesUniq.find(a => intersect(anneeC(a), anneeOf(ins))) || null : null;
      return { ...e, id, inscription: ins, filiere, niveau, annee, matched };
    });
  }, [etudiantsUniq, inscUniq, filieresUniq, niveauxUniq, anneesUniq, selAnneeDoc, selFiliereDoc, selNiveauGroup, filtreAnnee, filtreFiliere, filtreNiveau]);

  const filtered = useMemo(() => {
    const s = searchTerm.toLowerCase().trim();
    return enriched.filter(e => {
      if (!e.matched) return false;
      const nom = `${e.Nom || ''} ${e.Prénom || ''}`.toLowerCase();
      const cne = (e.CNE || '').toLowerCase();
      return !s || nom.includes(s) || cne.includes(s);
    });
  }, [enriched, searchTerm]);

  const toggleSelect = (e) => {
    setSelectedIds(prev => {
      const next = { ...prev };
      if (next[e.id]) delete next[e.id];
      else next[e.id] = true;
      return next;
    });
  };

  const handleChangeAnnee = (v) => {
    setFiltreAnnee(v);
    setFiltreFiliere('all');
    setFiltreNiveau('all');
  };

  const handleChangeFiliere = (v) => {
    setFiltreFiliere(v);
    setFiltreNiveau('all');
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const allSelected = filtered.every(e => prev[e.id]);
      if (allSelected) {
        const next = { ...prev };
        filtered.forEach(e => delete next[e.id]);
        return next;
      }
      const next = { ...prev };
      filtered.forEach(e => { next[e.id] = true; });
      return next;
    });
  };

  const toPrint = () => {
    const selected = filtered.filter(e => selectedIds[e.id]);
    return selected.length > 0 ? selected : filtered;
  };

  const carteHTML = (e) => {
    const img = photoCandidates(e)[0] || '';
    const nomComplet = `${e.Nom || ''} ${e.Prénom || ''}`.trim() || e.CNE || 'Étudiant';
    const filiere = getLabel(e.filiere);
    const niveau = getLabel(e.niveau);
    const annee = getLabel(e.annee);
    const numero = e.inscription?.numeroInscription || '-';
    const cne = e.CNE || '-';
    const sexe = e.sexe || '';
    const naissance = e.date_naissance || '';
    const sexeTxt = sexe === 'M' ? 'Masculin' : sexe === 'F' ? 'Féminin' : sexe;

    return `
      <div class="carte">
        <div class="carte-top">
          <img src="/images/logo_sup2i.png" class="carte-logo" onerror="this.style.display='none'" />
          <div class="carte-top-left">
            <div class="carte-titre">SUP2I</div>
            <div class="carte-soustitre">École Supérieure d'Informatique</div>
          </div>
          <div class="carte-badge">CARTE<br/>ÉTUDIANT</div>
        </div>
        <div class="carte-corps">
          <div class="carte-photo">${img ? `<img src="${esc(img)}" onerror="this.style.display='none';this.nextElementSibling.style.display='flex'" /><span style="display:none">${esc(initials(e))}</span>` : `<span>${esc(initials(e))}</span>`}</div>
          <div class="carte-infos">
            <div class="carte-nom">${esc(nomComplet)}</div>
            <div class="carte-classe">${esc(filiere)} · ${esc(niveau)}</div>
            <div class="carte-ligne"><strong>CNE :</strong> ${esc(cne)}</div>
            <div class="carte-ligne"><strong>N° Inscription :</strong> ${esc(numero)}</div>
            ${naissance ? `<div class="carte-ligne"><strong>Né(e) le :</strong> ${esc(naissance)}</div>` : ''}
            ${sexeTxt ? `<div class="carte-ligne"><strong>Sexe :</strong> ${esc(sexeTxt)}</div>` : ''}
          </div>
        </div>
        <div class="carte-pied">
          <span>Année universitaire ${esc(annee)}</span>
          <span class="carte-cne">${esc(cne)}</span>
        </div>
      </div>`;
  };

  const handlePrint = () => {
    const liste = toPrint();
    if (liste.length === 0) {
      setError('Aucune carte à imprimer');
      return;
    }
    const lignes = [];
    for (let i = 0; i < liste.length; i += 2) {
      lignes.push(`<div class="ligne">${carteHTML(liste[i])}${liste[i + 1] ? carteHTML(liste[i + 1]) : ''}</div>`);
    }

    const currentDate = new Date().toLocaleDateString('fr-FR');
    const printContent = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>Cartes étudiants - SUP2I</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; padding: 8mm; }
  .header { text-align: center; margin-bottom: 6mm; }
  .header h1 { color: #1f2937; font-size: 18px; }
  .header p { color: #6b7280; font-size: 11px; }
  .ligne { display: flex; gap: 5mm; margin-bottom: 6mm; page-break-inside: avoid; }
  .ligne::after { content: ''; display: block; height: 0; clear: both; }
  .carte { width: 92mm; height: 57mm; border: 1.5px solid #1e3a8a; border-radius: 4mm; overflow: hidden; background: #fff; position: relative; display: inline-block; flex-shrink: 0; }
  .carte-top { display: flex; align-items: center; gap: 3mm; background: linear-gradient(135deg, #6d28d9, #2563eb); color: #fff; padding: 3mm 4mm; }
  .carte-logo { width: 22px; height: 22px; border-radius: 4px; background: #fff; object-fit: contain; }
  .carte-top-left { flex: 1; }
  .carte-titre { font-weight: 800; font-size: 12px; letter-spacing: 0.02em; }
  .carte-soustitre { font-size: 8px; opacity: 0.92; }
  .carte-badge { font-size: 8px; text-align: right; line-height: 1.3; font-weight: 600; }
  .carte-corps { display: flex; gap: 4mm; padding: 4mm 4mm 2mm; }
  .carte-photo { width: 42px; height: 42px; border-radius: 2.5mm; background: #eef2ff; border: 1px solid #dbeafe; display: flex; align-items: center; justify-content: center; overflow: hidden; flex-shrink: 0; font-size: 13px; font-weight: 800; color: #4f46e5; }
  .carte-photo img { width: 100%; height: 100%; object-fit: cover; }
  .carte-infos { flex: 1; font-size: 8.5px; color: #374151; }
  .carte-nom { font-size: 11px; font-weight: 700; color: #111827; }
  .carte-classe { color: #6b7280; font-size: 8px; margin-bottom: 1mm; }
  .carte-ligne { margin-top: 0.7mm; }
  .carte-pied { display: flex; justify-content: space-between; align-items: center; border-top: 1px dashed #cbd5e1; padding: 1.5mm 4mm 2mm; color: #6b7280; font-size: 7.5px; position: absolute; bottom: 0; left: 0; right: 0; }
  .carte-cne { letter-spacing: 0.12em; font-weight: 600; }
  .footer { margin-top: 8mm; text-align: center; font-size: 9px; color: #9ca3af; }
  @media print {
    body { padding: 0; }
    .header { margin-bottom: 4mm; }
    .carte { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>
  <div class="header">
    <h1>🎓 SUP2I - Cartes Étudiants</h1>
    <p>Année universitaire 2026/2027 · Document généré le ${currentDate} · ${liste.length} carte(s)</p>
  </div>
  ${lignes.join('')}
  <div class="footer">SUP2I - École Supérieure d'Informatique · Carte valable pour l'année universitaire en cours</div>
</body>
</html>`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const selectCount = Object.keys(selectedIds).length;
  const printCount = toPrint().length;

  return (
    <div style={{ padding: '1.5rem 2rem', background: '#f3f4f6', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937' }}>🪪 Cartes Étudiants</h1>
          <p style={{ color: '#6b7280' }}>Génération et impression des cartes des étudiants</p>
        </div>
        <button onClick={handlePrint} style={{ padding: '0.75rem 1.5rem', background: '#3b82f6', border: 'none', borderRadius: '12px', color: 'white', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Printer size={18} /> Imprimer {selectCount > 0 ? `(${printCount} sélectionnée${printCount > 1 ? 's' : ''})` : `(${printCount})`}
        </button>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '1rem', borderRadius: '12px', marginBottom: '1.5rem', textAlign: 'center' }}>{error}</div>
      )}
      {info && (
        <div style={{ background: '#dcfce7', color: '#166534', padding: '1rem', borderRadius: '12px', marginBottom: '1.5rem', textAlign: 'center' }}>{info}</div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: '#6b7280' }}>Chargement des données…</div>
      ) : (
        <>
          <div style={{ background: 'white', borderRadius: '16px', padding: '1.25rem 1.5rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem' }}>
              <input
                type="text"
                placeholder="Rechercher par nom, prénom ou CNE…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ flex: 1, minWidth: '220px', padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
              <button onClick={toggleSelectAll} style={{ padding: '0.625rem 1rem', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', color: '#111827', fontWeight: '600', cursor: 'pointer' }}>Tout sélectionner</button>
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '180px' }}>
                <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '500' }}>Année universitaire</label>
                <select value={filtreAnnee} onChange={(e) => handleChangeAnnee(e.target.value)} style={{ width: '100%', padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                  <option value="all">Toutes les années</option>
                  {anneesDispo.map(a => <option key={getKey(a)} value={getKey(a)}>{getLabel(a)}</option>)}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: '180px' }}>
                <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '500' }}>Filière</label>
                <select value={filtreFiliere} onChange={(e) => handleChangeFiliere(e.target.value)} style={{ width: '100%', padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                  <option value="all">Toutes les filières</option>
                  {filieresDispo.map(f => <option key={getKey(f)} value={getKey(f)}>{getLabel(f)}</option>)}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: '180px' }}>
                <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '500' }}>Niveau</label>
                <select value={filtreNiveau} onChange={(e) => setFiltreNiveau(e.target.value)} style={{ width: '100%', padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                  <option value="all">Tous les niveaux</option>
                  {niveauxDispo.map(n => <option key={getKey(n)} value={getKey(n)}>{getLabel(n)}</option>)}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: '180px', display: 'flex', alignItems: 'flex-end' }}>
                <div style={{ width: '100%', padding: '0.625rem', background: '#f3f4f6', borderRadius: '8px', textAlign: 'center', color: '#374151', fontWeight: '600' }}>{filtered.length} étudiant(s)</div>
              </div>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', background: 'white', borderRadius: '16px', color: '#9ca3af' }}>Aucun étudiant trouvé</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.25rem', justifyContent: 'center' }}>
              {filtered.map(e => (
                <div key={e.id || e.CNE} style={{ display: 'flex', justifyContent: 'center' }}>
                  <CarteVisuelle e={e} selected={!!selectedIds[e.id]} onToggle={toggleSelect} onUpload={peutGererEtudiants ? uploadPhoto : undefined} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CartesEtudiants;